/**
 * Looking at the scene: where a world point is drawn on screen and back, pan and zoom, fitting
 * everything in view, and the 3D view's perspective (depth, fading, painting order).
 *
 * In 2D, `screen = world * view.k + view.{x,y}`. In 3D the camera (`camera.ts`) first projects
 * the world point into a flat picture: `screen = project(world) * view.k + view.{x,y}`. Every
 * other part goes through these functions, so clicking, dragging and drawing agree.
 */
import { frame, project, rayThrough, unproject, type Projected, type Vec3 } from "../camera";
import type { Scene } from "./index";
import type { SimNode } from "./types";
import { ZOOM } from "./constants";

/** Where the scene is drawn and how the pointer finds things in it. */
export interface SceneViewport {
  /** Fits every visible node (and group) in view. */
  fit(): void;
  /** Zooms by `factor` keeping the screen point under the cursor still. */
  zoomAt(px: number, py: number, factor: number): void;
  /** Centres the view on a node. */
  centerOn(id: string): void;
  /** Screen position of a node (horizontal). */
  sx(n: SimNode): number;
  /** Screen position of a node (vertical). */
  sy(n: SimNode): number;
  /** Radius of a node on screen (shrinks less than the zoom, and with the distance in 3D). */
  rs(n: SimNode): number;
  /** How much the perspective shrinks a point (1 in 2D, and at the camera's pivot in 3D). */
  depthScale(p: Vec3): number;
  /** How opaque a node is drawn: 1 in 2D; in 3D, nodes behind the middle fade into the background. */
  fade(n: SimNode): number;
  /** The visible nodes in the order they are painted: in 3D, the farthest first. */
  paintOrder(): SimNode[];
  /** Where a world point is drawn on screen (through the 3D perspective in 3D). */
  toScreen(p: { x: number; y: number; z?: number }): { x: number; y: number; scale: number };
  /** In 3D, the direction the camera looks along through a screen point (length 1); `null` in 2D. */
  rayAt(px: number, py: number): Vec3 | null;
  /** Converts a screen point to world coordinates (in 3D: on the plane through the camera's pivot). */
  toWorld(px: number, py: number): Vec3;
  /** The world point under a screen point, at the same depth as `at` (to drag `at` under the pointer). */
  planePoint(px: number, py: number, at: Vec3): Vec3;
  /** Moves a node so it is drawn under a screen point (keeping its depth in 3D). */
  dragTo(n: SimNode, px: number, py: number): void;
}

/** Where a world point is drawn in the flat picture, before pan and zoom (itself in 2D). */
export function flat(scene: Scene, p: Vec3 | { x: number; y: number; z?: number }): Projected {
  if (scene.dimension === "2d") return { x: p.x, y: p.y, scale: 1, depth: 0 };
  return project(scene.camera, { x: p.x, y: p.y, z: p.z ?? 0 });
}

/** The viewport of `scene`. */
export function viewport(scene: Scene): SceneViewport {
  return {
    fit() {
      const visible = scene.nodes.filter((n) => n.visible);
      if (!visible.length) return;
      if (scene.dimension === "3d") frame(scene.camera, visible);
      let x0 = Infinity;
      let y0 = Infinity;
      let x1 = -Infinity;
      let y1 = -Infinity;
      for (const n of visible) {
        const f = flat(scene, n);
        x0 = Math.min(x0, f.x);
        y0 = Math.min(y0, f.y);
        x1 = Math.max(x1, f.x);
        y1 = Math.max(y1, f.y);
      }
      if (scene.showGroups) {
        scene.layoutGroups();
        for (const g of scene.groups) {
          if (!g.visible) continue;
          const f = flat(scene, g);
          const r = g.r * f.scale;
          x0 = Math.min(x0, f.x - r);
          y0 = Math.min(y0, f.y - r);
          x1 = Math.max(x1, f.x + r);
          y1 = Math.max(y1, f.y + r);
        }
      }
      const pad = 56;
      const k = Math.min((scene.width - pad * 2) / Math.max(x1 - x0, 60), (scene.height - pad * 2) / Math.max(y1 - y0, 60));
      scene.view.k = Math.min(Math.max(k, ZOOM.min), ZOOM.fitMax);
      scene.view.x = scene.width / 2 - ((x0 + x1) / 2) * scene.view.k;
      scene.view.y = scene.height / 2 - ((y0 + y1) / 2) * scene.view.k;
    },

    zoomAt(px, py, factor) {
      const k = Math.min(Math.max(scene.view.k * factor, ZOOM.min), ZOOM.max);
      const f = k / scene.view.k;
      scene.view.x = px - (px - scene.view.x) * f;
      scene.view.y = py - (py - scene.view.y) * f;
      scene.view.k = k;
    },

    centerOn(id) {
      const n = scene.byId.get(id);
      if (!n) return;
      const f = flat(scene, n);
      scene.view.x = scene.width / 2 - f.x * scene.view.k;
      scene.view.y = scene.height / 2 - f.y * scene.view.k;
    },

    // In 2D straight from the node, with no projection made: these run for every node and link
    // end on every frame, 40 000 nodes and 80 000 links on a big project.
    sx: (n) => (scene.dimension === "2d" ? n.x : flat(scene, n).x) * scene.view.k + scene.view.x,
    sy: (n) => (scene.dimension === "2d" ? n.y : flat(scene, n).y) * scene.view.k + scene.view.y,
    rs: (n) => n.r * (0.55 + 0.45 * Math.min(Math.max(scene.view.k, 0.4), 2.5)) * (scene.dimension === "2d" ? 1 : flat(scene, n).scale),
    depthScale: (p) => flat(scene, p).scale,

    fade(n) {
      if (scene.dimension === "2d") return 1;
      // The front half is drawn fully; behind the middle, nodes fade quickly with the distance.
      return Math.min(Math.max(1 - (1 - flat(scene, n).scale) * 2.4, 0.18), 1);
    },

    paintOrder() {
      const visible = scene.nodes.filter((n) => n.visible);
      if (scene.dimension === "2d") return visible;
      const depth = new Map(visible.map((n) => [n, flat(scene, n).depth]));
      return visible.sort((a, b) => depth.get(b)! - depth.get(a)!);
    },

    toScreen(p) {
      const f = flat(scene, p);
      return { x: f.x * scene.view.k + scene.view.x, y: f.y * scene.view.k + scene.view.y, scale: f.scale };
    },

    rayAt: (px, py) => (scene.dimension === "3d" ? rayThrough(scene.camera, (px - scene.view.x) / scene.view.k, (py - scene.view.y) / scene.view.k) : null),

    toWorld: (px, py) => scene.planePoint(px, py, scene.camera.pivot),

    planePoint(px, py, at) {
      const fx = (px - scene.view.x) / scene.view.k;
      const fy = (py - scene.view.y) / scene.view.k;
      return scene.dimension === "2d" ? { x: fx, y: fy, z: 0 } : unproject(scene.camera, fx, fy, flat(scene, at).depth);
    },

    dragTo(n, px, py) {
      const w = scene.planePoint(px, py, n);
      Object.assign(n, { x: w.x, y: w.y, z: scene.dimension === "3d" ? w.z : 0, vx: 0, vy: 0, vz: 0 });
    },
  };
}
